/* Care+ observations only. This module never mutates a cart, dialog or navigation. */
(() => {
  'use strict';
  const element = document.getElementById('pascucci-care-config');
  if (!element || window.PUICareAnalytics) return;
  let config;
  try { config = JSON.parse(element.textContent); } catch (_) { return; }
  const endpoint = 'https://analytics.capula.co/care/events';
  const revision = 'care-monitor-v1';
  const isTest = config.analyticsTest === true;
  const sessionKey = 'pui-care-analytics-session-v1';
  let active = null;
  let memorySession = null;
  let consentGeneration = 0;
  const allowed = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch (_) { return false; }
  };
  const id = () => crypto.randomUUID();
  function session() {
    const now = Date.now();
    let value = memorySession;
    try { value = JSON.parse(sessionStorage.getItem(sessionKey)); } catch (_) { /* Storage may be disabled. */ }
    if (!value || now - value.last > 30 * 60 * 1000) value = { id: id(), last: now };
    value.last = now;
    try { sessionStorage.setItem(sessionKey, JSON.stringify(value)); } catch (_) { /* In-memory identity is sufficient. */ }
    memorySession = value;
    return value.id;
  }
  function transmit(event) {
    if (!allowed() || config.analyticsEnabled === false) return;
    // The same event ID is sent by the consent-aware pixel bridge for deduplication.
    try { window.Shopify?.analytics?.publish('pui_care:' + event.event_type, event); } catch (_) { /* Independent delivery path. */ }
    const body = JSON.stringify(event);
    try {
      if (['pagehide', 'visibility', 'checkout_redirect'].includes(event.event_type) && navigator.sendBeacon) {
        if (navigator.sendBeacon(endpoint, new Blob([body], { type: 'text/plain' }))) return;
      }
      const send = remaining => {
        if (!allowed()) return;
        fetch(endpoint, { method: 'POST', credentials: 'omit', keepalive: true,
          headers: { 'Content-Type': 'text/plain' }, body })
          .then(response => {
            if ((response.status === 429 || response.status >= 500) && remaining > 0)
              setTimeout(() => send(remaining - 1), 1000);
          }).catch(() => { if (remaining > 0) setTimeout(() => send(remaining - 1), 1000); });
      };
      send(2);
    } catch (_) { /* Commerce always continues. */ }
  }
  async function digest(token) {
    if (!token) return null;
    const bytes = new TextEncoder().encode(String(token).split('?')[0]);
    const hash = await crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(hash)].map(x => x.toString(16).padStart(2, '0')).join('');
  }
  function begin(product, quantity) {
    if (!allowed() || config.analyticsEnabled === false) return null;
    const context = { id: id(), session: session(), product, cartRef: null,
      shown: false, terminal: false, inactive: false, generation: consentGeneration, lastActivity: Date.now() };
    const record = (eventType, extra = {}) => {
      if (!allowed() || context.generation !== consentGeneration) return;
      const event = Object.assign({ schema_version: 1, event_id: id(), event_type: eventType,
        attempt_id: context.id, session_id: context.session, product: context.product,
        occurred_at: new Date().toISOString(), device: matchMedia('(max-width:768px)').matches ? 'mobile' : 'desktop',
        is_test: isTest, revision }, context.cartRef ? { cart_ref: context.cartRef } : {}, extra);
      if (eventType === 'checkout_redirect' && context.decision) event.reason = context.decision;
      transmit(event);
    };
    const tracker = {
      record,
      cart(cart) {
        // Hash asynchronously; no caller awaits telemetry or sends a cart access key.
        digest(cart?.token).then(ref => { context.cartRef = ref; }).catch(() => {});
      },
      shown() { context.shown = true; context.lastActivity = Date.now(); active = { context, tracker }; record('offer_shown'); },
      finish(reason) { context.terminal = true; if (reason) context.decision = reason; if (active?.context === context) active = null; }
    };
    record('eligible_atc', { quantity: Number(quantity) || 1 });
    return tracker;
  }
  function activity() {
    if (!active || !allowed() || document.visibilityState !== 'visible') return;
    const { context, tracker } = active;
    context.lastActivity = Date.now();
    if (context.inactive) { context.inactive = false; tracker.record('resumed'); }
  }
  ['pointerdown', 'keydown', 'touchstart'].forEach(name => document.addEventListener(name, activity, { passive: true, capture: true }));
  setInterval(() => {
    if (!active || !allowed() || document.visibilityState !== 'visible') return;
    const { context, tracker } = active;
    if (Date.now() - context.lastActivity >= 30000) {
      if (!context.inactive) { context.inactive = true; tracker.record('inactive'); }
    } else tracker.record('activity');
  }, 15000);
  document.addEventListener('visibilitychange', () => {
    if (!allowed()) return;
    if (active) active.tracker.record('visibility', { visibility: document.visibilityState });
  });
  window.addEventListener('pagehide', () => { if (active) active.tracker.record('pagehide'); });
  document.addEventListener('visitorConsentCollected', () => {
    if (!allowed()) {
      active = null;
      memorySession = null;
      consentGeneration++;
      try { sessionStorage.removeItem(sessionKey); } catch (_) { /* Optional analytics storage. */ }
    }
  });
  try {
    window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
  } catch (_) { /* Unavailable privacy API means collection remains disabled. */ }
  window.PUICareAnalytics = { begin };
})();
